import { useQuery } from '@tanstack/react-query';

import { useHttpClient, type HttpResponse } from '@bonusly/http-client';

import { ResourcePath } from '../hooks/resource-path';
import { QueryKey, QueryOptions } from '../hooks/types';
import { RecognizeResponse } from '../types/api';
import { CoreValue } from '../types/core-value';
import { HashtagTrend } from '../types/hashtag';

type Format = 'core_value_hashtags' | 'index' | 'summary';

interface SummaryResponse {
	trends: {
		custom: HashtagTrend[];
		values: HashtagTrend[];
	};
}

interface AllHashtagsResponse {
	values: CoreValue[];
}

type ListResponse = string[];
type ResponseType<T> = T extends 'summary' ? SummaryResponse : T extends 'core_value_hashtags' ? AllHashtagsResponse : ListResponse;
type IndexResponse<T = Format> = RecognizeResponse<ResponseType<T>>;

interface IndexArgs<T extends Format = Format> {
	params: {
		display_format?: T;
	};
}

export type HashtagIndexResponse<T = Format> = HttpResponse<IndexResponse<T>>;

export const useHashtags = <T extends Format = Format>(args: IndexArgs<T>, options?: QueryOptions<HashtagIndexResponse<T>>) => {
	const http = useHttpClient('v2');
	return useQuery<HashtagIndexResponse<T>>(
		[QueryKey.Hashtags, args],
		() =>
			http.get<IndexResponse<T>, HashtagIndexResponse>(ResourcePath.Hashtags, {
				params: args.params,
			}),
		options
	);
};
